import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { LeadDialog } from "@/components/LeadDialog";
import { Eyebrow } from "@/components/Reveal";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/hooks/useData";
import { imgUrl } from "@/lib/api";

const fade = (d) => ({
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay: d, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = () => {
  const { content } = useContent();
  const c = content.hero;
  return (
    <section className="relative pt-28 sm:pt-36 pb-12 sm:pb-20 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end">
          <div className="lg:col-span-5">
            <motion.div {...fade(0)}>
              <Eyebrow>{c.eyebrow}</Eyebrow>
            </motion.div>
            <motion.h1 {...fade(0.1)} className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#2C2A29] mt-6">
              {c.title_line1}
              <br />
              <span className="italic text-[#9E8773]">{c.title_accent}</span>{c.title_suffix}
            </motion.h1>
            <motion.p {...fade(0.2)} className="mt-7 text-lg text-[#5E5A54] font-light leading-relaxed max-w-md">
              {c.subtitle}
            </motion.p>
            <motion.div {...fade(0.3)} className="mt-9 flex flex-wrap gap-4">
              <Button asChild className="rounded-none bg-[#2C2A29] hover:bg-[#4A4641] text-[#FFFFFF] h-12 px-8 label-eyebrow text-[10px]">
                <Link to="/store" data-testid="hero-explore-button">{c.primary_cta}</Link>
              </Button>
              <LeadDialog
                trigger={
                  <Button variant="outline" className="rounded-none border-[#2C2A29] text-[#2C2A29] hover:bg-[#F6F5F2] h-12 px-8 label-eyebrow text-[10px] group" data-testid="hero-quote-button">
                    {c.secondary_cta}
                    <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                }
              />
            </motion.div>
            <motion.div {...fade(0.4)} className="mt-10 pt-8 border-t border-[#ECEAE4]">
              <p className="label-eyebrow text-[10px] text-[#9E8773]">{c.trusted_label}</p>
              <p className="mt-2 text-sm text-[#5E5A54] font-light tracking-wide">{c.trusted_text}</p>
            </motion.div>
          </div>

          <motion.div {...fade(0.2)} className="lg:col-span-7">
            <div className="img-frame group aspect-[4/3] lg:aspect-[16/11] border border-[#ECEAE4]">
              <img src={imgUrl(c.image_url)} alt="Luxurious modern interior" className="w-full h-full object-cover" />
            </div>
            <p className="mt-3 label-eyebrow text-[10px] text-[#9E8773] text-right">{c.image_caption}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
